import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Slider,
  styled,
  Typography,
} from "@material-ui/core";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SkipNextIcon from "@material-ui/icons/SkipNext";
import SkipPreviousIcon from "@material-ui/icons/SkipPrevious";
import { useCallback, useEffect, useRef, useState } from "react";
import { EPOCUS_WHITE, rem } from "../epocus-palette";
import { type CineMetadata, cineFrameRate } from "./cine-metadata";
import { useCineSource } from "./use-cine-source";

const PlayerRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
});

const Stage = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Video = styled("video")({
  maxWidth: "calc(100vw - 88px)",
  maxHeight: "calc(100vh - 240px)",
  objectFit: "contain",
  backgroundColor: "#000",
  display: "block",
});

const StatusOverlay = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  padding: "10px 12px",
  backgroundColor: "rgba(0,0,0,0.62)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
});

const StatusText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_WHITE,
});

const RetryButton = styled(Button)({
  alignSelf: "flex-start",
  minHeight: 44,
  color: EPOCUS_WHITE,
  borderColor: "rgba(255,255,255,0.5)",
  fontSize: rem(13),
  textTransform: "none",
});

const LoadBar = styled(LinearProgress)({
  height: 4,
  borderRadius: 2,
  backgroundColor: "rgba(255,255,255,0.2)",
});

const Controls = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  width: "100%",
  padding: "0 4px",
});

const ControlButton = styled(IconButton)({
  color: EPOCUS_WHITE,
  width: 44,
  height: 44,
  "&:disabled": { color: "rgba(255,255,255,0.25)" },
});

const FrameSlider = styled(Slider)({
  color: EPOCUS_WHITE,
  marginLeft: 8,
  marginRight: 8,
  padding: "16px 0",
  "& .MuiSlider-rail": { opacity: 0.3 },
});

const FrameCounter = styled(Typography)({
  fontSize: rem(12),
  color: "rgba(255,255,255,0.7)",
  whiteSpace: "nowrap",
  minWidth: 76,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
});

const STATUS_MESSAGES = {
  TRANSCODING: "Preparing loop…",
  FAILED: "Loop couldn't be loaded.",
  UNAVAILABLE: "No loop available for this image.",
} as const;

export const EpocusCinePlayer: FC<{
  imagingInstanceId: string;
  metadata: CineMetadata;
  posterUrl?: string;
  label: string;
  active: boolean;
}> = ({ imagingInstanceId, metadata, posterUrl, label, active }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(1);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [isPlayable, setIsPlayable] = useState(false);

  const { state, src, progress, retry, reportPlaybackError } = useCineSource({
    imagingInstanceId,
    availability: metadata.availability,
    sizeBytes: metadata.sizeBytes,
    enabled: active,
  });

  const framesPerSecond = cineFrameRate(metadata, durationSeconds ?? undefined);

  const seekToFrame = useCallback(
    (frame: number) => {
      const video = videoRef.current;
      if (!video || !framesPerSecond) return;
      const clamped = Math.min(Math.max(frame, 1), metadata.numberOfFrames);
      video.pause();
      video.currentTime = (clamped - 0.5) / framesPerSecond;
      setCurrentFrame(clamped);
    },
    [framesPerSecond, metadata.numberOfFrames],
  );

  useEffect(() => {
    if (!isPlaying || !framesPerSecond) return;
    let handle: number;
    const tick = () => {
      const video = videoRef.current;
      if (video) {
        setCurrentFrame(
          Math.min(
            metadata.numberOfFrames,
            Math.floor(video.currentTime * framesPerSecond) + 1,
          ),
        );
      }
      handle = requestAnimationFrame(tick);
    };
    handle = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(handle);
  }, [isPlaying, framesPerSecond, metadata.numberOfFrames]);

  useEffect(() => {
    setIsPlayable(false);
    const video = videoRef.current;
    if (!video || !src) return;
    void video.play()?.catch(() => undefined);
  }, [src]);

  const isReady = state === "READY" && isPlayable;
  const isDownloading =
    state === "LOADING" || (state === "READY" && !isPlayable);
  const statusMessage = isReady
    ? null
    : isDownloading
      ? progress === null
        ? "Loading loop…"
        : `Loading loop… ${Math.round(progress * 100)}%`
      : STATUS_MESSAGES[state as keyof typeof STATUS_MESSAGES];

  return (
    <PlayerRoot>
      <Stage>
        <Video
          ref={videoRef}
          src={src ?? undefined}
          poster={posterUrl}
          aria-label={`${label} cine loop`}
          muted
          loop
          playsInline
          preload="auto"
          onLoadedMetadata={(event) =>
            setDurationSeconds(event.currentTarget.duration)
          }
          onCanPlay={() => setIsPlayable(true)}
          onError={() => src && reportPlaybackError()}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
        {statusMessage && (
          <StatusOverlay>
            <StatusText>{statusMessage}</StatusText>
            {(isDownloading || state === "TRANSCODING") && (
              <LoadBar
                variant={
                  isDownloading && progress !== null
                    ? "determinate"
                    : "indeterminate"
                }
                value={progress === null ? undefined : progress * 100}
              />
            )}
            {state === "FAILED" && (
              <RetryButton variant="outlined" size="small" onClick={retry}>
                Retry
              </RetryButton>
            )}
          </StatusOverlay>
        )}
      </Stage>

      <Controls>
        <ControlButton
          aria-label={isPlaying ? "Pause loop" : "Play loop"}
          disabled={!isReady}
          onClick={() => {
            const video = videoRef.current;
            if (!video) return;
            if (video.paused) void video.play()?.catch(() => undefined);
            else video.pause();
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </ControlButton>
        <ControlButton
          aria-label="Previous frame"
          disabled={!isReady || !framesPerSecond}
          onClick={() => seekToFrame(currentFrame - 1)}
        >
          <SkipPreviousIcon />
        </ControlButton>
        <ControlButton
          aria-label="Next frame"
          disabled={!isReady || !framesPerSecond}
          onClick={() => seekToFrame(currentFrame + 1)}
        >
          <SkipNextIcon />
        </ControlButton>
        <FrameSlider
          aria-label="Frame"
          disabled={!isReady || !framesPerSecond}
          min={1}
          max={metadata.numberOfFrames}
          step={1}
          value={currentFrame}
          valueLabelDisplay="off"
          aria-valuetext={`Frame ${currentFrame} of ${metadata.numberOfFrames}`}
          onChange={(_event, value) => seekToFrame(value as number)}
        />
        <FrameCounter data-testid="cine-frame-counter">
          {currentFrame} / {metadata.numberOfFrames}
        </FrameCounter>
      </Controls>
    </PlayerRoot>
  );
};
